/**
 * Particle state as a plain ref — zero React re-renders from particle updates.
 * Physics and culling happen inside the RAF loop in GameCanvas.
 */

import { useRef, useCallback } from 'react';
import type { Particle } from '../utils/types';
import { PARTICLE_CAP } from '../utils/constants';

export interface UseParticlesReturn {
  particlesRef: React.MutableRefObject<Particle[]>;
  addParticles: (newParticles: Particle[]) => void;
  clearParticles: () => void;
}

export function useParticles(): UseParticlesReturn {
  const particlesRef = useRef<Particle[]>([]);

  const addParticles = useCallback((newParticles: Particle[]) => {
    particlesRef.current.push(...newParticles);
    // Drop oldest when over cap
    if (particlesRef.current.length > PARTICLE_CAP) {
      particlesRef.current = particlesRef.current.slice(-PARTICLE_CAP);
    }
  }, []);

  const clearParticles = useCallback(() => {
    particlesRef.current = [];
  }, []);

  return { particlesRef, addParticles, clearParticles };
}
